import { useEffect, useState } from "react";
import { ArrowLeftRight, MapPin } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { hoyISO } from "@/lib/format";
import { useEmpresa } from "@/hooks/useEmpresa";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

function franjasTxt(franjas) {
  return franjas?.map((f) => `${f.inicio}–${f.fin}`).join(" · ") || "—";
}

function JornadaCard({ titulo, detalle, esCompanero }) {
  if (!detalle) return null;
  const disponible = detalle.disponible;

  return (
    <div className={`rounded-xl border p-3 ${esCompanero ? "border-violet-200 bg-violet-50/50" : "border-indigo-200 bg-indigo-50/50"}`}>
      <p className="text-[10px] font-bold uppercase tracking-wide text-zinc-500">{titulo}</p>
      {detalle.usuario && <p className="mt-0.5 text-sm font-semibold text-zinc-800">{detalle.usuario}</p>}

      {!disponible ? (
        <p className="mt-2 text-xs text-amber-700">
          {detalle.ausencia?.label || detalle.motivo || "No disponible"}
        </p>
      ) : (
        <>
          <p className="mt-2 text-sm font-medium text-indigo-900">
            {detalle.turno_nombre || franjasTxt(detalle.franjas)}
          </p>
          {detalle.franjas?.length > 0 && detalle.turno_nombre?.includes("–") === false && (
            <p className="mt-0.5 text-xs text-indigo-700/80">{franjasTxt(detalle.franjas)}</p>
          )}
          {detalle.zonas?.length > 0 && (
            <div className="mt-2 space-y-1">
              <p className="flex items-center gap-1 text-[10px] font-semibold uppercase text-zinc-500">
                <MapPin className="h-3 w-3" />
                Zonas asignadas
              </p>
              {detalle.zonas.map((z) => (
                <div
                  key={z.id}
                  className="rounded px-2 py-1 text-[11px] font-medium"
                  style={{ borderLeft: `3px solid ${z.zona_color}`, backgroundColor: `${z.zona_color}15` }}
                >
                  <span style={{ color: z.zona_color }}>{z.zona_nombre}</span>
                  <span className="ml-1 text-zinc-500">{z.hora_inicio}–{z.hora_fin}</span>
                </div>
              ))}
            </div>
          )}
          {detalle.zonas?.length === 0 && (
            <p className="mt-1 text-[10px] text-zinc-400">Sin zonas asignadas ese día</p>
          )}
        </>
      )}
    </div>
  );
}

export default function IntercambioTurnoDialog({ open, onOpenChange, onSuccess }) {
  const { empresa } = useEmpresa();
  const [companeros, setCompaneros] = useState([]);
  const [detalleMio, setDetalleMio] = useState(null);
  const [detalleCompanero, setDetalleCompanero] = useState(null);
  const [form, setForm] = useState({
    fecha: hoyISO(),
    companero_id: "",
    motivo: "",
  });
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/intercambios/companeros")
      .then(({ data }) => setCompaneros(data))
      .catch(() => setCompaneros([]));
  }, [open]);

  useEffect(() => {
    if (!open || !form.fecha) return;
    api.get("/intercambios/detalle-jornada", { params: { fecha: form.fecha } })
      .then(({ data }) => setDetalleMio(data))
      .catch(() => setDetalleMio(null));
  }, [open, form.fecha]);

  useEffect(() => {
    if (!open || !form.companero_id || !form.fecha) {
      setDetalleCompanero(null);
      return;
    }
    api.get("/intercambios/detalle-jornada", {
      params: { fecha: form.fecha, usuario_id: form.companero_id },
    })
      .then(({ data }) => setDetalleCompanero(data))
      .catch(() => setDetalleCompanero(null));
  }, [open, form.companero_id, form.fecha]);

  const puedeEnviar = detalleMio?.disponible && detalleCompanero?.disponible && form.companero_id && form.motivo.trim().length >= 3;

  const enviar = async () => {
    if (!form.motivo.trim() || form.motivo.trim().length < 3) {
      toast.error("Indica el motivo del intercambio");
      return;
    }
    if (!form.companero_id) {
      toast.error("Selecciona un compañero de tu departamento");
      return;
    }
    if (!detalleMio?.disponible) {
      toast.error("No tienes jornada laborable ese día");
      return;
    }
    if (!detalleCompanero?.disponible) {
      toast.error("El compañero no está disponible ese día");
      return;
    }
    setEnviando(true);
    try {
      await api.post("/intercambios/turno", {
        motivo: form.motivo.trim(),
        fecha_inicio: form.fecha,
        fecha_fin: form.fecha,
        fecha_propia: form.fecha,
        fecha_companero: form.fecha,
        usuario_intercambio_id: Number(form.companero_id),
      });
      toast.success("Solicitud enviada. Tu compañero y el encargado serán notificados.");
      onSuccess?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Intercambiar turno con otro trabajador</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="space-y-1.5">
            <Label>Día del intercambio</Label>
            <Input
              type="date"
              value={form.fecha}
              onChange={(e) => setForm({ ...form, fecha: e.target.value, companero_id: "" })}
            />
          </div>

          <div className="space-y-1.5">
            <Label>Compañero (mismo departamento)</Label>
            <select
              className="h-9 w-full rounded-md border border-zinc-200 px-2"
              value={form.companero_id}
              onChange={(e) => setForm({ ...form, companero_id: e.target.value })}
            >
              <option value="">Selecciona compañero…</option>
              {companeros.map((c) => (
                <option key={c.id} value={c.id}>{c.nombre}</option>
              ))}
            </select>
            {companeros.length === 0 && (
              <p className="text-xs text-amber-700">No hay otros trabajadores en tu departamento.</p>
            )}
          </div>

          <JornadaCard titulo="Tu jornada" detalle={detalleMio} />

          {form.companero_id && (
            <>
              <div className="flex justify-center">
                <ArrowLeftRight className="h-5 w-5 text-violet-500" />
              </div>
              <JornadaCard titulo="Turno real del compañero" detalle={detalleCompanero} esCompanero />
            </>
          )}

          {detalleMio?.disponible && detalleCompanero?.disponible && detalleMio.turno_id !== detalleCompanero.turno_id && (
            <p className="rounded-lg border border-sky-100 bg-sky-50 px-3 py-2 text-xs text-sky-800">
              Los horarios son distintos: al aprobar el intercambio se ajustarán los turnos de ambos para ese día.
            </p>
          )}

          {empresa?.gestionar_zonas && detalleCompanero?.disponible && (
            <p className="rounded-lg border border-fuchsia-100 bg-fuchsia-50 px-3 py-2 text-xs text-fuchsia-800">
              Si alguno tiene zonas asignadas ese día, también se intercambiarán al aprobar el cambio.
            </p>
          )}

          <div className="space-y-1.5">
            <Label>Motivo</Label>
            <Textarea value={form.motivo} onChange={(e) => setForm({ ...form, motivo: e.target.value })} rows={3} />
          </div>
          <p className="text-xs text-zinc-500">
            Primero deberá aceptar tu compañero; después el encargado revisará el cambio.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={enviar} disabled={enviando || !puedeEnviar} className="bg-indigo-600 hover:bg-indigo-700">
            {enviando ? "Enviando…" : "Solicitar intercambio"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
